import { module } from 'modujs';
import gsap from 'gsap';

const MIN_QUERY_LENGTH = 4;
const DEBOUNCE_MS = 800;
const DEFAULT_LIMIT = 5;
const API_ENDPOINT = '/api/search';
const MAX_CACHED_QUERIES = 50;

// Shared by every search input on the page; each API call is billed by Google.
const resultsCache = new Map();

export default class extends module {
    constructor(m) {
        super(m);

        this.lang = this.getData('lang');
        this.baseUrl = this.getData('url');
        this.token = this.getData('token') || '';
        this.resultsId = this.getData('results-id') || null;
        this.limit = parseInt(this.getData('limit'), 10) || DEFAULT_LIMIT;
        this.minLength = parseInt(this.getData('min-length'), 10) || MIN_QUERY_LENGTH;
        this.debounceMs = parseInt(this.getData('debounce'), 10) || DEBOUNCE_MS;

        this.debounceTimer = null;
        this.placeholderTl = null;
        this.abortController = null;
        this.requestId = 0;

        this.events = {
            input: {
                input: 'onInput'
            },
            focusin: {
                input: 'onFocus'
            },
            focusout: {
                input: 'onBlur'
            },
            submit: {
                form: 'onSubmit'
            }
        };
    }

    init() {
        this.$input = this.$('input')[0];

        const placeholders = this.$input.dataset.discoverySearchInputPlaceholders
            || this.$input.dataset.searchInputPlaceholders;
        if (placeholders && !this.$input.value.trim()) {
            const list = placeholders.split('|').map(s => s.trim()).filter(Boolean);
            if (list.length > 0) {
                this._initPlaceholderAnimation(list);
            }
        }

        if (this.$input.value.trim()) {
            this.el.classList.add('has-value');
        }

        this.onClickOutsideBind = this.onClickOutside.bind(this);
        this.onEscapeBind = this.onEscape.bind(this);

        document.addEventListener('click', this.onClickOutsideBind);
        document.addEventListener('keyup', this.onEscapeBind);
    }

    _initPlaceholderAnimation(list) {
        const wrap = document.createElement('div');
        wrap.className = 'c-search-input_fake-placeholder';
        wrap.setAttribute('aria-hidden', 'true');

        const spans = list.map(word => {
            const span = document.createElement('span');
            span.className = 'c-search-input_fake-word';
            span.textContent = word;
            wrap.appendChild(span);
            return span;
        });

        this.$('form')[0].appendChild(wrap);
        this.$fakePlaceholder = wrap;

        gsap.set(spans, { yPercent: 110 });

        this.placeholderTl = gsap.timeline({
            repeat: -1,
            delay: 0.5,
            defaults: { ease: 'power2.out', duration: 0.5 }
        });

        spans.forEach(span => {
            this.placeholderTl
                .to(span, { yPercent: 0 })
                .to(span, { yPercent: -110 }, '+=2.5')
                .set(span, { yPercent: 110 });
        });
    }

    onFocus() {
        if (this.placeholderTl) {
            this.placeholderTl.pause();
            this.el.classList.add('is-focused');
        }

        const query = this.$input.value.trim();
        if (query.length >= this.minLength) {
            this.onInput();
        }
    }

    onBlur() {
        if (this.placeholderTl && !this.$input.value.trim()) {
            this.el.classList.remove('is-focused');
            this.placeholderTl.resume();
        }
    }

    onInput() {
        clearTimeout(this.debounceTimer);
        this.abortPendingRequest();

        const query = this.$input.value.trim();

        this.el.classList.toggle('has-value', !!query);

        if (query.length < this.minLength) {
            this.el.classList.remove('is-loading');
            this.callResults('clear', null);
            return;
        }

        const cacheKey = this.cacheKey(query);
        if (resultsCache.has(cacheKey)) {
            ++this.requestId;
            this.el.classList.remove('is-loading');
            this.showResults(resultsCache.get(cacheKey), query);
            return;
        }

        this.debounceTimer = setTimeout(async () => {
            this.el.classList.add('is-loading');
            const currentRequestId = ++this.requestId;
            const result = await this.fetchResults(query);

            if (!result || currentRequestId !== this.requestId) {
                return;
            }

            this.el.classList.remove('is-loading');
            this.showResults(result, query);
        }, this.debounceMs);
    }

    showResults(result, query) {
        this.callResults('setResults', {
            hits: result.hits,
            nbHits: result.nbHits,
            query,
            baseUrl: this.baseUrl,
            lang: this.lang
        });
    }

    cacheKey(query) {
        return `${this.lang || 'fr'}|${this.limit}|${query.toLowerCase().replace(/\s+/g, ' ')}`;
    }

    cacheResult(query, result) {
        const key = this.cacheKey(query);
        resultsCache.delete(key);
        resultsCache.set(key, result);

        if (resultsCache.size > MAX_CACHED_QUERIES) {
            resultsCache.delete(resultsCache.keys().next().value);
        }
    }

    onSubmit(e) {
        e.preventDefault();

        const query = this.$input.value.trim();

        this.abortPendingRequest();
        this.callResults('clear', null);

        if (query) {
            window.location.href = `${this.baseUrl}?s=${encodeURIComponent(query)}`;
        }
    }

    onClickOutside(e) {
        if (!this.el.contains(e.target) && !document.documentElement.classList.contains('has-search-open')) {
            this.callResults('clear', null);
        }
    }

    onEscape(e) {
        if (e.key === 'Escape') {
            this.callResults('clear', null);
        }
    }

    callResults(method, args) {
        if (this.resultsId) {
            this.call(method, args, 'SearchResults', this.resultsId);
        } else {
            this.call(method, args, 'SearchResults');
        }
    }

    abortPendingRequest() {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }

    async fetchResults(query) {
        this.abortPendingRequest();
        this.abortController = new AbortController();

        try {
            const params = new URLSearchParams({
                query,
                limit: String(this.limit),
                lang: this.lang || 'fr'
            });

            const response = await fetch(`${API_ENDPOINT}?${params.toString()}`, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'X-Search-Token': this.token
                },
                signal: this.abortController.signal
            });

            if (!response.ok) {
                return { hits: [], nbHits: 0 };
            }

            const data = await response.json();

            if (data.error || !Array.isArray(data.results)) {
                return { hits: [], nbHits: 0 };
            }

            const hits = data.results.map(result => ({
                title: result.title || '',
                url: result.link || '#'
            }));

            // Discovery does not return a total beyond the page; treat a full page as "maybe more".
            const nbHits = hits.length >= this.limit ? hits.length + 1 : hits.length;
            const result = { hits, nbHits };

            this.cacheResult(query, result);

            return result;
        } catch (error) {
            if (error?.name === 'AbortError') {
                return null;
            }

            return { hits: [], nbHits: 0 };
        } finally {
            this.abortController = null;
        }
    }

    destroy() {
        clearTimeout(this.debounceTimer);
        this.abortPendingRequest();
        if (this.placeholderTl) this.placeholderTl.kill();
        document.removeEventListener('click', this.onClickOutsideBind);
        document.removeEventListener('keyup', this.onEscapeBind);
    }
}
